import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Open records, as tabs.
 *
 * ── Why this state is in memory and nowhere else ────────────────────────────────────
 * opioid-demo's page-tabs.js keeps its tabs in sessionStorage. For this product that would
 * be a durable, XSS-readable list of which patient records a user opened, outliving the
 * page that made it. Nothing here is written to sessionStorage or localStorage.
 *
 * "Clears on logout" is not cleanup code that could be forgotten, either: the provider is
 * mounted inside RequireAuth, which swaps its children for a redirect the moment the
 * session ends. The state is cleared by the component ceasing to exist. The same trade the
 * access token already makes in lib/auth.tsx — memory only, continuity from the cookie.
 *
 * ── The one rule ────────────────────────────────────────────────────────────────────
 * At MAX_TABS, opening another is refused: no eviction, no reordering, nothing touched.
 * The demo calls tabs.shift() and silently drops the oldest, which is the behaviour this
 * deliberately does not have — a reviewer who queued up ten records should not lose one
 * because they clicked an eleventh.
 */

export const MAX_TABS = 10;

export const TAB_LIMIT_NOTICE = `Only ${MAX_TABS} tabs allowed — close a tab to open another.`;

export type RecordKind =
  | 'participant'
  | 'report'
  | 'review'
  | 'facility'
  | 'user'
  | 'measureEntry'
  | 'family'
  | 'case'
  | 'ingestBatch';

/** Where each kind of record lives, and where to fall back to when its last tab closes. */
const LIST_PATH: Record<RecordKind, string> = {
  participant: '/participants',
  report: '/reports',
  review: '/review-queue',
  facility: '/facilities',
  user: '/admin/users',
  measureEntry: '/measures',
  family: '/families',
  case: '/cases',
  ingestBatch: '/ingest',
};

const DEFAULT_LABEL: Record<RecordKind, string> = {
  participant: 'Participant',
  report: 'Report',
  review: 'Review item',
  facility: 'Facility',
  user: 'User',
  measureEntry: 'Measure',
  family: 'Family',
  case: 'Case',
  ingestBatch: 'Import',
};

export function recordPath(kind: RecordKind, id: string): string {
  return `${LIST_PATH[kind]}/${id}`;
}

export interface RecordTab {
  /** `${kind}:${id}` — what makes opening the same record twice a switch, not a duplicate. */
  key: string;
  kind: RecordKind;
  id: string;
  path: string;
  label: string;
}

export interface OpenRecordInput {
  kind: RecordKind;
  id: string;
  /** Absent when the caller does not know the code yet; the detail page fills it in later. */
  label?: string | undefined;
}

export type OpenOutcome = 'opened' | 'switched' | 'refused';

interface TabsContextValue {
  tabs: RecordTab[];
  notice: string | null;
  openRecord: (input: OpenRecordInput) => OpenOutcome;
  registerTab: (input: OpenRecordInput) => void;
  /** Removes the tab and returns where to go *if* it was the one being viewed. */
  closeTab: (key: string) => string | null;
  closeAll: () => void;
  dismissNotice: () => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function tabFor(input: OpenRecordInput): RecordTab {
  return {
    key: `${input.kind}:${input.id}`,
    kind: input.kind,
    id: input.id,
    path: recordPath(input.kind, input.id),
    label: input.label?.trim() || DEFAULT_LABEL[input.kind],
  };
}

export function TabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<RecordTab[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  /** Non-blocking by construction: it announces itself and then goes away on its own. */
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const openRecord = useCallback(
    (input: OpenRecordInput): OpenOutcome => {
      const next = tabFor(input);
      const existing = tabs.find((t) => t.key === next.key);

      if (existing) {
        // Already open. Refresh the label if the caller knows a better one, and let the
        // click through — switching to a tab is what opening an open record means.
        if (input.label && input.label !== existing.label) {
          setTabs((prev) =>
            prev.map((t) => (t.key === next.key ? { ...t, label: next.label } : t)),
          );
        }
        return 'switched';
      }

      if (tabs.length >= MAX_TABS) {
        setNotice(TAB_LIMIT_NOTICE);
        return 'refused';
      }

      // Newest on the left; everything else shifts right.
      setTabs((prev) => [next, ...prev]);
      return 'opened';
    },
    [tabs],
  );

  /**
   * The arrival path: a pasted link, a reload, or Back into a record whose tab was closed.
   *
   * Unlike openRecord this cannot refuse anything — the record is already on screen and
   * refusing to *display* a URL somebody typed would be broken. At the cap it says so and
   * adds no tab.
   */
  const registerTab = useCallback(
    (input: OpenRecordInput) => {
      openRecord(input);
    },
    [openRecord],
  );

  const closeTab = useCallback(
    (key: string): string | null => {
      const index = tabs.findIndex((t) => t.key === key);
      if (index === -1) return null;

      const closed = tabs[index]!;
      const remaining = tabs.filter((t) => t.key !== key);
      setTabs(remaining);

      // The tab that slid into its place, else the one before it, else that record kind's
      // list — never a record with no tab representing it.
      const neighbour = remaining[index] ?? remaining[index - 1];
      return neighbour ? neighbour.path : LIST_PATH[closed.kind];
    },
    [tabs],
  );

  const closeAll = useCallback(() => setTabs([]), []);
  const dismissNotice = useCallback(() => setNotice(null), []);

  const value = useMemo(
    () => ({ tabs, notice, openRecord, registerTab, closeTab, closeAll, dismissNotice }),
    [tabs, notice, openRecord, registerTab, closeTab, closeAll, dismissNotice],
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function useTabs(): TabsContextValue {
  const value = useContext(TabsContext);
  if (!value) throw new Error('useTabs must be used inside a TabsProvider');
  return value;
}

/**
 * Registers the record a detail page is showing, and keeps its label current.
 *
 * Called with the code once the query lands, so a tab opened by a pasted URL starts as
 * "Participant" and becomes "PTC-000123". Guarded on a signature rather than on the
 * callback identity — registerTab changes on every tab change, and depending on it would
 * re-register on every unrelated open and close.
 */
export function useRegisterTab(input: OpenRecordInput | null): void {
  const { registerTab } = useTabs();

  const register = useRef(registerTab);
  register.current = registerTab;

  const signature = input ? `${input.kind}:${input.id}:${input.label ?? ''}` : '';

  useEffect(() => {
    if (!signature) return;
    const [kind, id, ...rest] = signature.split(':');
    const label = rest.join(':');
    register.current({
      kind: kind as RecordKind,
      id: id!,
      ...(label ? { label } : {}),
    });
  }, [signature]);
}
